import _ from 'underscore';
import Phaser from 'phaser';
import {isIOS} from '../utils/PhaserUtils';

const KEY_CODES = {
    ArrowLeft: 37,
    ArrowRight: 39,
    Enter: 13,
    Backspace: 8,
    Delete: 46
};

class TextInput {
    defaultStyles = {
        align: 'center',
        fill: '#FFFF00', //text color
        fontSize: 20,
        colorAlpha: 1, //textOpacity
        background: 0x000000, //
        backgroundAlpha: 1,
        borderRadius: 0,
        borderColor: 0xFFFFFF,
        borderColorAlpha: 1,
        borderWidth: 0
    }

    defaultOptions = {
        uppercase: false,
        lettersOnly: false,
        maxLength: -1,
        onEnterPressed: null,
        onInput: null
    }

    regularBackground = null;
    focusBackground = null;
    text = null;
    align = null;

    constructor(game, parent, name, value, styles, options) {
        styles = styles || {};

        this.game = game;
        this.focused = false;
        this.active = false;
        this.options = {...this.defaultOptions, ...options};

        this.onKeyPress = this.onKeyPress.bind(this);
        this.onMouseDown = this.onMouseDown.bind(this);
        this.onMouseDownOutside = this.onMouseDownOutside.bind(this);
        this.onWindowTouchEnd = this.onWindowTouchEnd.bind(this);
        this.onWindowMouseUp = this.onWindowMouseUp.bind(this);

        const regularStyles = {...this.defaultStyles, ...styles.regular};
        const graphics = this.game.add.graphics(0, 0);

        graphics.inputEnabled = true;

        this.align = regularStyles.align;
        this.regularBackground = this.renderBackground(regularStyles);

        graphics.addChild(this.regularBackground);

        this.setBackground(this.regularBackground);

        if (styles.focus) {
            const focusStyles = {...regularStyles, ...styles.focus};

            this.focusBackground = this.renderBackground(focusStyles);
            graphics.addChild(this.focusBackground);
        }

        this.textGroup = this.renderValue(regularStyles, value);
        graphics.addChild(this.textGroup);

        this.updateValuePosition();

        graphics.beginFill(0, 0);
        graphics.drawRect(0, 0, this.regularBackground.width, this.regularBackground.height);
        graphics.endFill();

        graphics.events.onInputDown.add( this.onMouseDown);
        this.game.input.onDown.add(this.onMouseDownOutside);

        window.document.addEventListener('mouseup', this.onWindowMouseUp);
        window.document.addEventListener('touchend', this.onWindowTouchEnd);

        graphics.events.onDestroy.add(() => {
            graphics.events.onInputDown.remove( this.onMouseDown);
            this.game.input.onDown.remove(this.onMouseDownOutside);
            this.removeFocusser();
            window.document.removeEventListener('touchend', this.onWindowTouchEnd);
            window.document.removeEventListener('mouseup', this.onWindowMouseUp);
        });

        if (parent) {
            parent.add(graphics);
        }

        this._graphics =  graphics;
    }

    setFocusser(y = '75%') {
        this.removeFocusser();
        const input = window.document.createElement('input');

        window.document.body.appendChild(input);

        Object.assign(input.style, {
            height: '10%',
            width: '50%',
            opacity: 0,
            fontSize: '20px',
            position: 'fixed',
            top: y,
            left: '25%',
            pointerEvents: 'none'
        });

        input.addEventListener('keyup', this.onKeyPress, false);

        input.focus();

        if (isIOS()) {
            const scrollTop = Number(y.replace('px', ''));

            if (!isNaN(scrollTop)) {
                window.document.documentElement.scrollTop = scrollTop;
            }
        }

        this.focusser = input;
    }

    removeFocusser() {
        if (this.focusser) {
            this.focusser.removeEventListener('keyup', this.onKeyPress);
            this.focusser.remove();
            delete this.focusser;
        }
    }

    getWidth() {
        return this.regularBackground.width;
    }

    getGraphics() {
        return this._graphics;
    }

    getValue() {
        return this.textGroup.getChildAt(0).text;
    }

    onWindowMouseUp(e) {
        if (this.focused && !this.focusser) {
            this.setFocusser(e.clientY + 'px');
        }
    }

    onWindowTouchEnd(e) {
        if (this.focused && !this.focusser) {
            const touch = e.changedTouches[0];

            if (touch) {
                this.setFocusser(touch.clientY + 'px');
            } else {
                this.setFocusser();
            }
        }
    }

    onMouseDown() {
        if (this.focusBackground) {
            this.setBackground(this.focusBackground);
        }

        this.focused = true;
        this.updateValuePosition();
    }

    onMouseDownOutside() {
        this.focused = false;
        this.removeFocusser();
        this.updateValuePosition();
        this.setBackground(this.regularBackground);
    }

    onKeyPress(e) {
        if (this.focused) {
            if (e.keyCode === KEY_CODES.ArrowLeft) {
                this.changeCursorPosition(-1);
            } else if (e.keyCode === KEY_CODES.ArrowRight) {
                this.changeCursorPosition(1);
            } else if (e.keyCode === KEY_CODES.Enter && this.options.onEnterPressed) {
                this.blur();
                this.options.onEnterPressed(this);
            } else if (e.keyCode === KEY_CODES.Backspace) {
                this.removeBeforeCursor();
            } else if (e.keyCode === KEY_CODES.Delete) {
                this.removeAfterCursor();
            } else if (e.key.length === 1 || e.keyCode === 229 /* cover Android bug */) {
                let key = e.key;

                if (key.length > 1) {
                    key = this.focusser.value;
                }

                if (!this.options.lettersOnly || (/^[a-z]*$/i).test(key)) {
                    this.insertValue(this.options.uppercase ? key.toUpperCase() : key);
                }
            }
            _.defer(() => {
                // clear value to be able get last input value wor Android workaround

                if (this.focusser) {
                    this.focusser.value = '';
                }
            });
        }
    }

    insertValue(value) {
        const wholeText = this.textGroup.getChildAt(0);
        const beforeCursor = this.textGroup.getChildAt(1);
        const splitIndex = beforeCursor.text.length;

        if (this.options.maxLength > 0 && wholeText.text.length >= this.options.maxLength) {
            return;
        }

        wholeText.text = wholeText.text.substring(0, splitIndex) + value + wholeText.text.substring(splitIndex);
        beforeCursor.text += value;

        this.updateValuePosition();

        if (this.options.onInput) {
            this.options.onInput(this);
        }
    }

    removeBeforeCursor() {
        const wholeText = this.textGroup.getChildAt(0);
        const beforeCursor = this.textGroup.getChildAt(1);
        const splitIndex = beforeCursor.text.length;

        if (splitIndex > 0) {
            beforeCursor.text = beforeCursor.text.substring(0, splitIndex - 1);
            wholeText.text = wholeText.text.substring(0, splitIndex - 1) + wholeText.text.substring(splitIndex);
            this.updateValuePosition();

            if (this.options.onInput) {
                this.options.onInput(this);
            }
        }
    }

    removeAfterCursor() {
        const wholeText = this.textGroup.getChildAt(0);
        const splitIndex = this.textGroup.getChildAt(1).text.length;

        if (splitIndex < wholeText.text.length) {
            wholeText.text = wholeText.text.substring(0, splitIndex) + wholeText.text.substring(splitIndex + 1);
            this.updateValuePosition();

            if (this.options.onInput) {
                this.options.onInput(this);
            }
        }
    }

    changeCursorPosition(delta) {
        const value = this.getValue();
        const beforeCursor = this.textGroup.getChildAt(1);
        const cursorPosition = beforeCursor.text.length;

        if (delta < 0 && cursorPosition > 0) {
            delta = Math.max(delta, -cursorPosition);
            const splitIndex = cursorPosition + delta;

            beforeCursor.text = beforeCursor.text.substring(0, splitIndex);
        } else if (delta > 0 && value.length > cursorPosition) {
            delta = Math.min(delta, value.length - cursorPosition);
            beforeCursor.text += value.substring(cursorPosition, cursorPosition + delta);
        } else {
            return;
        }

        this.updateValuePosition();
    }

    renderText(x, y, value, styles) {
        return this.game.add.text(x, y, value, styles);
    }

    renderValue(styles, value) {
        const cursorHeight = styles.cursorHeight || styles.height - styles.fontSize / 3;
        const cursorWidth = styles.cursorWidth || Math.ceil(styles.fontSize / 20);
        const textWrapper = this.game.add.group();
        const hexFill = this.ensureHexColor(styles.fill);
        const wholeText = this.renderText(0, 0, value, styles);
        const beforeCursor = this.renderText(0, 0, value, styles);
        const cursor = this.drawRoundedRect(0, (styles.height - cursorHeight) / 2, cursorWidth, cursorHeight, 0, 0, 0, 0, hexFill, 0.6);
        this.game.add.tween(cursor).to({alpha: 0.3}, 200, Phaser.Easing.Linear.None, true, 0, 10000, true);

        beforeCursor.visible = false;
        wholeText.smoothed = true;

        textWrapper.add(wholeText);
        textWrapper.add(beforeCursor);
        textWrapper.add(cursor);

        return textWrapper;
    }

    updateValuePosition() {
        const wholeText = this.textGroup.getChildAt(0);
        const beforeCursor = this.textGroup.getChildAt(1);
        const cursor = this.textGroup.getChildAt(2);

        cursor.x = beforeCursor.width;
        cursor.visible = this.focused;

        switch (this.align) {
            case 'left':
                this.textGroup.x = 0;
                break;
            case 'center':
                this.textGroup.x = Math.ceil((this.regularBackground.width - wholeText.width) / 2);
                break;
            case 'right':
                this.textGroup.x = this.regularBackground.width - wholeText.width;
                break;
        }
    }

    drawRoundedRect(x, y, width, height, radius, bSize, bColor, bAlpha, fColor, fAlpha) {
        const rect = this.game.add.graphics(0, 0);
        rect.name = _.uniqueId('rect_');
        rect.lineStyle(bSize, bColor, bAlpha);
        rect.beginFill(fColor, fAlpha);
        if (radius > 0) {
            rect.drawRoundedRect(x, y, width, height, radius);
        } else {
            rect.drawRect(x, y, width, height);
        }
        rect.endFill();

        return rect;
    }

    setBackground(background) {
        if (background && this.currentBackground !== background) {
            if (this.currentBackground) {
                this.currentBackground.visible = false;
            }

            background.visible = true;
            this.currentBackground = background;
        }
    }

    renderBackground(styles) {
        const buttonRect = this.drawRoundedRect(
            0,
            0,
            styles.width,
            styles.height,
            styles.borderRadius,
            styles.borderWidth,
            styles.borderColor,
            styles.borderColorAlpha,
            styles.background,
            styles.backgroundAlpha
        );

        buttonRect.visible = false;

        return buttonRect;
    }

    ensureHexColor(color) {
        return typeof color === 'string' ? parseInt(color.replace('#', '0x')) : color;
    }

    focus() {
        this.onMouseDown();
        this.setFocusser();
    }

    blur() {
        this.removeFocusser();
    }
}

export default TextInput;



// WEBPACK FOOTER //
// ./app/src/display/TextInput.js